import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShieldCheck, ShieldOff, Loader2, History, Film } from 'lucide-react';

const fmtTs = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

/**
 * Master approval + versioning. An approved Master is an IMMUTABLE,
 * numbered snapshot (backend: MasterApprovalSnapshot) — approving never
 * touches the working composition's own tracks/items, it only freezes a
 * copy and flips a status flag; revoking (or resuming editing, which
 * auto-flips this to "draft" server-side) never deletes that frozen
 * copy. Every previously approved version stays listed below forever.
 */
export function EditorMasterApprovalPanel({
  approvalStatus, currentApprovedVersion, activeSnapshot, versions,
  onApprove, onRevoke, approving,
}) {
  const isApproved = approvalStatus === 'approved';

  return (
    <div className="space-y-3" data-testid="master-approval-panel">
      <div className="p-3 rounded-lg bg-white/[0.03] border border-white/10 space-y-2">
        <div className="flex items-center justify-between">
          <span className="flex items-center gap-2 text-sm text-white">
            {isApproved ? <ShieldCheck className="h-4 w-4 text-green-400" /> : <ShieldOff className="h-4 w-4 text-[#8A93A6]" />}
            {isApproved ? `Approved · v${currentApprovedVersion}` : 'Draft'}
          </span>
          <Badge
            className={`${isApproved ? 'bg-green-500/10 text-green-400' : 'bg-white/10 text-[#8A93A6]'} border-0 text-[10px]`}
            data-testid="master-approval-status-badge"
          >
            {isApproved ? 'Ready for clip generation' : 'Not approved'}
          </Badge>
        </div>

        {isApproved && activeSnapshot && (
          <p className="text-[11px] text-[#8A93A6]" data-testid="master-approval-meta">
            Approved {fmtTs(activeSnapshot.approved_at)} · {activeSnapshot.source_ids?.length || 0} source{(activeSnapshot.source_ids?.length || 0) !== 1 ? 's' : ''} authorized
            {activeSnapshot.brand_snapshot?.name ? ` · Brand Kit "${activeSnapshot.brand_snapshot.name}" frozen` : ''}
          </p>
        )}
        {!isApproved && currentApprovedVersion && (
          <p className="text-[11px] text-amber-300/80" data-testid="master-approval-draft-notice">
            Editing resumed since v{currentApprovedVersion} was approved — that version is preserved untouched; approve again to freeze a new one.
          </p>
        )}

        <div className="flex gap-2 pt-1">
          <Button
            size="sm" disabled={approving} onClick={onApprove}
            className="flex-1 bg-green-600 hover:bg-green-600/90 text-white h-8 text-xs"
            data-testid="approve-master-button"
          >
            {approving ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <ShieldCheck className="h-3.5 w-3.5 mr-1.5" />}
            {isApproved ? 'Re-approve (new version)' : 'Approve Master'}
          </Button>
          {isApproved && (
            <Button
              size="sm" variant="outline" disabled={approving} onClick={onRevoke}
              className="border-white/10 h-8 text-xs" data-testid="revoke-master-button"
            >
              Revoke
            </Button>
          )}
        </div>
      </div>

      {versions?.length > 0 && (
        <div className="space-y-1.5" data-testid="master-version-history">
          <p className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-[#555E6E]">
            <History className="h-3 w-3" />Version history
          </p>
          {[...versions].reverse().map((v) => (
            <div
              key={v.version}
              className="flex items-center justify-between p-2 rounded-md bg-white/5 border border-white/10 text-[11px]"
              data-testid={`master-version-row-${v.version}`}
            >
              <span className="flex items-center gap-1.5 text-white">
                <Film className="h-3 w-3 text-[#8A93A6]" />v{v.version}
              </span>
              <span className="text-[#8A93A6]">{fmtTs(v.approved_at)}</span>
              <Badge className={`${v.revoked_at ? 'bg-white/10 text-[#8A93A6]' : 'bg-green-500/10 text-green-400'} border-0 text-[9px]`}>
                {v.revoked_at ? 'Revoked' : 'Active'}
              </Badge>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
